import { useState, useCallback, useRef, useEffect } from 'react';
import { useOkapiKy } from '@folio/stripes/core';
import {
  uploadAsset,
  createJob,
  getJob,
  getJobEvents,
  getProposals,
  getChangeSet,
  submitReview,
  applyToForm,
  cancelJob,
  openJobStream,
} from '../services/catalogingAgentService';

export const JOB_STATUS = {
  IDLE:             'idle',
  UPLOADING:        'uploading',
  CREATED:          'created',
  PREPARING:        'preparing',
  EXTRACTING:       'extracting',
  ENRICHING:        'enriching',
  VALIDATING:       'validating',
  NEEDS_INFORMATION:'needs_information',
  AWAITING_REVIEW:  'awaiting_review',
  READY_TO_APPLY:   'ready_to_apply',
  APPLIED_TO_FORM:  'applied_to_form',
  COMPLETED:        'completed',
  FAILED:           'failed',
  CANCELLED:        'cancelled',
};

const POLLING_INTERVAL = 3000; // 3s fallback polling

const useCatalogingAgent = ({ templateId, instanceId, onApplyChanges }) => {
  const ky = useOkapiKy();

  const [status, setStatus]         = useState(JOB_STATUS.IDLE);
  const [job, setJob]               = useState(null);
  const [proposals, setProposals]   = useState([]);
  const [changeSet, setChangeSet]   = useState(null);
  const [events, setEvents]         = useState([]);
  const [error, setError]           = useState(null);
  const [isLoading, setIsLoading]   = useState(false);

  const streamRef   = useRef(null);
  const pollingRef  = useRef(null);
  const lastSeqRef  = useRef(0);

  // ── Cleanup ───────────────────────────────────────────────────────────────
  const stopTracking = useCallback(() => {
    if (streamRef.current)  { streamRef.current.close(); streamRef.current = null; }
    if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
  }, []);

  useEffect(() => () => stopTracking(), [stopTracking]);

  // ── Polling fallback ──────────────────────────────────────────────────────
  const startPolling = useCallback((jobId) => {
    if (pollingRef.current) return;
    pollingRef.current = setInterval(async () => {
      try {
        const [jobData, newEvents] = await Promise.all([
          getJob(ky, jobId),
          getJobEvents(ky, jobId, lastSeqRef.current),
        ]);

        setJob(jobData);
        setStatus(jobData.status);

        if (newEvents.length) {
          lastSeqRef.current = newEvents[newEvents.length - 1].sequence;
          setEvents(prev => [...prev, ...newEvents]);
        }

        // Stop polling when terminal or awaiting review
        const terminal = ['completed', 'failed', 'cancelled', 'awaiting_review', 'applied_to_form'];
        if (terminal.includes(jobData.status)) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;

          if (jobData.status === 'awaiting_review') {
            const props = await getProposals(ky, jobId);
            setProposals(props);
          }
        }
      } catch (err) {
        setError(err.message);
      }
    }, POLLING_INTERVAL);
  }, [ky]);

  // ── Start job ─────────────────────────────────────────────────────────────
  const startJob = useCallback(async (file) => {
    try {
      setError(null);
      setEvents([]);
      setProposals([]);
      setChangeSet(null);
      lastSeqRef.current = 0;

      // 1. Upload asset
      setStatus(JOB_STATUS.UPLOADING);
      setIsLoading(true);
      const asset = await uploadAsset(ky, file);

      // 2. Create job
      const newJob = await createJob(ky, {
        templateId,
        assetIds: [asset.id],
        instanceId,
      });

      setJob(newJob);
      setStatus(newJob.status);
      setIsLoading(false);

      // 3. Start tracking
      startPolling(newJob.id);

      return newJob;
    } catch (err) {
      setError(err.message);
      setStatus(JOB_STATUS.FAILED);
      setIsLoading(false);
      return null;
    }
  }, [ky, templateId, instanceId, startPolling]);

  // ── Submit review ─────────────────────────────────────────────────────────
  const submitDecisions = useCallback(async (decisions) => {
    if (!job) return;
    try {
      setIsLoading(true);
      await submitReview(ky, job.id, { decisions, revision: job.revision });

      // Get change set
      const cs = await getChangeSet(ky, job.id);
      setChangeSet(cs);
      setStatus(JOB_STATUS.READY_TO_APPLY);
      setIsLoading(false);
    } catch (err) {
      setError(err.message);
      setIsLoading(false);
    }
  }, [ky, job]);

  // ── Apply to form ─────────────────────────────────────────────────────────
  const applyChanges = useCallback(async () => {
    if (!job || !changeSet) return;
    try {
      setIsLoading(true);
      await applyToForm(ky, job.id, changeSet.fingerprint);
      setStatus(JOB_STATUS.APPLIED_TO_FORM);

      // Pass approved changes to parent form
      if (onApplyChanges && changeSet.items) {
        onApplyChanges(changeSet.items);
      }
      setIsLoading(false);
    } catch (err) {
      setError(err.message);
      setIsLoading(false);
    }
  }, [ky, job, changeSet, onApplyChanges]);

  // ── Cancel ────────────────────────────────────────────────────────────────
  const cancel = useCallback(async () => {
    if (!job) return;
    stopTracking();
    try {
      await cancelJob(ky, job.id);
      setStatus(JOB_STATUS.CANCELLED);
    } catch { /* ignore */ }
  }, [ky, job, stopTracking]);

  // ── Reset ─────────────────────────────────────────────────────────────────
  const reset = useCallback(() => {
    stopTracking();
    setStatus(JOB_STATUS.IDLE);
    setJob(null);
    setProposals([]);
    setChangeSet(null);
    setEvents([]);
    setError(null);
    setIsLoading(false);
    lastSeqRef.current = 0;
  }, [stopTracking]);

  return {
    status,
    job,
    proposals,
    changeSet,
    events,
    error,
    isLoading,
    isIdle:           status === JOB_STATUS.IDLE,
    isRunning:        !['idle', 'completed', 'failed', 'cancelled', 'applied_to_form'].includes(status),
    isAwaitingReview: status === JOB_STATUS.AWAITING_REVIEW,
    isReadyToApply:   status === JOB_STATUS.READY_TO_APPLY,
    isTerminal:       ['completed', 'failed', 'cancelled', 'applied_to_form'].includes(status),
    startJob,
    submitDecisions,
    applyChanges,
    cancel,
    reset,
  };
};

export default useCatalogingAgent;
